import React from 'react';
import { useNavigate } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-lg font-black text-white uppercase tracking-tight mb-4 pb-3 border-b border-white/[0.06]">{title}</h2>
    <div className="space-y-3 text-gray-400 text-sm leading-relaxed">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-10 h-10 object-contain"
            />
            <h1 className="text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
          </div>
          <button onClick={() => navigate(-1)} className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest transition-colors">← Back</button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 md:px-6 pt-32 pb-24">
        <div className="mb-12">
          <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-2">Legal</p>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-3">Privacy Policy</h1>
          <p className="text-gray-500 text-sm">Last updated: April 18, 2026</p>
        </div>

        <Section title="1. Introduction">
          <p>EventDeck ("we," "our," or "us") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform at eventdeck.app and related services.</p>
          <p>By using EventDeck, you agree to the collection and use of information in accordance with this policy. If you disagree with any part of this policy, please discontinue use of our services.</p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong className="text-white">Personal Information:</strong> When you register for an event or create an account, we collect your full name, email address, phone number, and payment transaction IDs.</p>
          <p><strong className="text-white">Payment Information:</strong> We collect mobile banking transaction IDs (bKash, Nagad, Rocket) submitted for payment verification. We do not store full payment credentials or card numbers.</p>
          <p><strong className="text-white">Usage Data:</strong> We automatically collect information about how you interact with our platform, including IP addresses, browser type, pages visited, and timestamps.</p>
          <p><strong className="text-white">Event-Specific Data:</strong> Organisers may add custom fields to registration forms. Any information you submit in these fields is collected and stored.</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use collected information to: process event registrations and issue digital tickets; verify payment transactions; send confirmation emails and QR code tickets; communicate important event updates; improve our platform and services; and comply with legal obligations.</p>
          <p>We do not sell, trade, or rent your personal information to third parties for marketing purposes.</p>
        </Section>

        <Section title="4. Data Sharing">
          <p><strong className="text-white">With Event Organisers:</strong> Registration data including your name, email, phone, and submitted form responses is shared with the event organiser you registered with. Organisers are bound by their own privacy obligations.</p>
          <p><strong className="text-white">Service Providers:</strong> We may share data with trusted service providers (email delivery, hosting) who assist in operating our platform under strict confidentiality agreements.</p>
          <p><strong className="text-white">Legal Requirements:</strong> We may disclose your information if required by law or in response to valid requests from public authorities.</p>
        </Section>

        <Section title="5. Data Retention">
          <p>We retain your personal data for as long as necessary to provide our services and comply with legal obligations. Registration records are typically retained for 2 years after the event date. You may request deletion of your account and associated data at any time.</p>
        </Section>

        <Section title="6. Security">
          <p>We implement industry-standard security measures including HTTPS encryption, secure database storage, and access controls. However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security of your data.</p>
        </Section>

        <Section title="7. Your Rights">
          <p>You have the right to: access the personal information we hold about you; request correction of inaccurate data; request deletion of your data; object to processing of your data; and withdraw consent at any time.</p>
          <p>To exercise these rights, contact us at privacy@eventdeck.app.</p>
        </Section>

        <Section title="8. Cookies">
          <p>We use essential cookies to maintain your session and preferences. We do not use tracking or advertising cookies. You can control cookies through your browser settings.</p>
        </Section>

        <Section title="9. Children's Privacy">
          <p>Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such information, please contact us immediately.</p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="11. Contact Us">
          <p>If you have questions about this Privacy Policy, please contact us at:</p>
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-5 mt-3">
            <p className="text-white font-bold">EventDeck</p>
            <p>Email: privacy@eventdeck.app</p>
            <p>Support: support@eventdeck.app</p>
          </div>
        </Section>
      </main>
    </div>
  );
};

export default PrivacyPolicy;